import { Heart, Phone, Video, Star, MessageCircle, User, Calendar } from 'lucide-react';
import Link from 'next/link';
import Image from 'next/image';
import {
  getExpertAvailability,
  getExpertRate,
  getExpertName,
  getExpertExpertise,
  getExpertBio,
  getExpertPhoto,
  formatRate,
  parseExpertCallModes,
} from '../../lib/expertUtils';

export default function PersonCard({
  expert,
  onCall,
  onViewProfile,
  canMakeVideoCalls,
  isFavorited,
  onToggleFavorite,
  favoriteLoading,
  canFavorite,
  onChat,
  showActions = true,
  onPhotoError,
}) {
  const availability = getExpertAvailability(expert);
  const rate = getExpertRate(expert);
  const name = getExpertName(expert);
  const expertise = getExpertExpertise(expert);
  const bio = getExpertBio(expert);
  const { supportsAudio, supportsVideo } = parseExpertCallModes(expert);
  // Chat is default-on; only hidden if an expert explicitly sets chatEnabled === false (they can disable from profile)
  const explicitlyDisabled = expert.chatEnabled === false || expert.expertProfile?.chatEnabled === false;
  const supportsChat = !explicitlyDisabled;
  const rating = expert.averageRating || expert.rating || 0;
  const totalCalls = Math.max(
    Number(expert.totalCalls || 0),
    Number(expert.callsCompleted || 0),
    Number(expert.totalReviews || expert.reviewCount || expert.ratingCount || 0),
    Number(expert.reviewSummary?.totalReviews || expert.reviewSummary?.count || 0),
    Number(expert.averageRating || expert.rating || expert.reviewSummary?.averageRating || 0) > 0 ? 1 : 0
  );
  const favoriteCount = Number(expert.seekerFavoritesCount || 0);
  const photoURL = getExpertPhoto(expert);
  const isOnline = availability === 'online';
  const isBusy = availability === 'busy';
  const isOffline = availability === 'offline';
  const isApproved = expert.applicationStatus === 'approved';
  const isTrusted = expert.trustedPayoutVerified === true;

  return (
    <div
      className={`group cursor-pointer bg-white dark:bg-gray-800 rounded-2xl overflow-hidden shadow-lg
        hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1
        ${isOffline ? 'opacity-70' : ''}`}
      onClick={() => onViewProfile(expert)}
    >
      {/* Photo Section */}
      <div className="relative aspect-[4/5] overflow-hidden bg-gradient-to-br from-emerald-100 to-blue-100 dark:from-emerald-900/30 dark:to-blue-900/30">
        {/* Availability Badge */}
        {isOnline && (
          <div className="absolute top-3 right-3 z-10 flex items-center gap-1.5 bg-emerald-500/90 backdrop-blur-sm px-2.5 py-1 rounded-full">
            <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
            <span className="text-white text-xs font-bold">LIVE</span>
          </div>
        )}
        {isBusy && (
          <div className="absolute top-3 right-3 z-10 flex items-center gap-1.5 bg-orange-500/90 backdrop-blur-sm px-2.5 py-1 rounded-full">
            <span className="text-white text-xs font-bold">BUSY</span>
          </div>
        )}
        {!isApproved && (
          <div className="absolute top-3 right-3 z-10 flex items-center gap-1.5 bg-violet-600/90 backdrop-blur-sm px-2.5 py-1 rounded-full">
            <span className="text-white text-xs font-bold">NEW</span>
          </div>
        )}

        {photoURL ? (
          <Image
            src={photoURL}
            alt={name}
            fill
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
            className="object-cover group-hover:scale-105 transition-transform duration-500"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              onPhotoError?.(expert, photoURL);
            }}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-emerald-400 to-blue-500 flex items-center justify-center">
              <span className="text-white text-3xl font-bold">
                {name.charAt(0).toUpperCase()}
              </span>
            </div>
          </div>
        )}

        {/* Gradient Overlay */}
        <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />

        {/* Name + Expertise overlay */}
        <div className="absolute bottom-3 left-3 right-3 text-white z-[1]">
          <div className="flex items-center gap-2 min-w-0">
            <h3 className="font-bold text-lg truncate leading-tight">{name}</h3>
            {isTrusted && (
              <span title="Successfully completed paid work and received a verified payout" className="shrink-0 inline-flex items-center gap-1 rounded-full bg-amber-400 px-2 py-0.5 text-[10px] font-bold text-amber-950">
                ★
              </span>
            )}
          </div>
          <p className="text-sm text-white/85 truncate">{expertise}</p>
        </div>

        {/* Favorite action */}
        {canFavorite && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggleFavorite(expert);
            }}
            disabled={favoriteLoading}
            className={`absolute top-3 left-3 z-10 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-semibold backdrop-blur-sm transition-colors ${
              isFavorited
                ? 'bg-rose-600/95 text-white'
                : 'bg-white/90 text-gray-800 hover:bg-white'
            } ${favoriteLoading ? 'opacity-70 cursor-wait' : ''}`}
          >
            <Heart className={`w-3.5 h-3.5 ${isFavorited ? 'fill-current' : ''}`} />
            {favoriteCount}
          </button>
        )}
      </div>

      {/* Info Section */}
      <div className="p-4">
        {/* Rating + Rate Row */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-1">
            <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
            <span className="font-semibold text-sm text-gray-900 dark:text-gray-100">
              {rating > 0 ? Number(rating).toFixed(1) : 'New'}
            </span>
            {totalCalls > 0 && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                ({totalCalls})
              </span>
            )}
          </div>
          <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
            <Heart className="w-3.5 h-3.5" />
            <span>{favoriteCount}</span>
          </div>
          <div className="text-right">
            <span className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
              ${formatRate(rate)}
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400">/min</span>
          </div>
        </div>

        {/* Bio - visible on hover (desktop) and always on mobile */}
        {bio && (
          <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mb-3
            sm:opacity-0 sm:max-h-0 sm:group-hover:opacity-100 sm:group-hover:max-h-16
            sm:transition-all sm:duration-300 sm:overflow-hidden">
            {bio}
          </p>
        )}

        {/* Call Action Buttons — only for seekers */}
        {showActions && (
          <div className="flex gap-2">
            {isApproved && supportsAudio && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onCall(expert, 'audio');
                }}
                disabled={isOffline || isBusy}
                className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl
                  bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold
                  transition-all active:scale-95
                  disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-emerald-600"
              >
                <Phone className="w-4 h-4" />
                Audio
              </button>
            )}
            {isApproved && supportsVideo && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onCall(expert, 'video');
                }}
                disabled={isOffline || isBusy || !canMakeVideoCalls}
                className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl
                  bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold
                  transition-all active:scale-95
                  disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-blue-600"
              >
                <Video className="w-4 h-4" />
                Video
              </button>
            )}
            {supportsChat && onChat && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onChat(expert);
                }}
                className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl
                  bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold
                  transition-all active:scale-95
                  disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-purple-600"
              >
                <MessageCircle className="w-4 h-4" />
                Chat
              </button>
            )}
            {/* Fallback if no modes set - show both */}
            {isApproved && !supportsAudio && !supportsVideo && !supportsChat && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onCall(expert, 'audio');
                }}
                disabled={isOffline || isBusy}
                className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl
                  bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold
                  transition-all active:scale-95
                  disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-emerald-600"
              >
                <Phone className="w-4 h-4" />
                Call
              </button>
            )}
          </div>
        )}

        {/* Schedule + View Profile row. Schedule sends the seeker to the
            profile with ?openBooking=true, which the profile's existing
            effect auto-opens into the BookingModal so they skip the extra
            click. When showActions is off (public / logged-out view),
            View Profile is the primary CTA and Schedule sits alongside. */}
        <div className={`flex gap-2 ${showActions ? 'mt-2' : ''}`}>
          {isApproved && <Link
            href={`/expert-profile/${expert.uid || expert.id}?openBooking=true`}
            onClick={(e) => e.stopPropagation()}
            aria-label={`Schedule a call with ${name}`}
            title="Schedule a call"
            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-sm font-semibold transition-all active:scale-95 border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-900/30"
          >
            <Calendar className="w-4 h-4" />
            Schedule
          </Link>}
          {isApproved && <Link
            href={`/expert-profile/${expert.uid || expert.id}`}
            onClick={(e) => e.stopPropagation()}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-sm font-semibold transition-all active:scale-95 ${
              showActions
                ? 'border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                : 'bg-emerald-600 hover:bg-emerald-700 text-white'
            }`}
          >
            <User className="w-4 h-4" />
            View Profile
          </Link>}
        </div>

        {!isApproved && <p className="mt-2 text-center text-xs font-medium text-violet-700 dark:text-violet-300">New — not yet taking calls or bookings</p>}
        {/* Offline message */}
        {isOffline && (
          <p className="text-center text-xs text-gray-400 dark:text-gray-500 mt-2">
            Currently offline
          </p>
        )}
      </div>
    </div>
  );
}
